import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  CircularProgress,
  CssBaseline,
} from "@mui/material";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

const TRACKING_API = "https://dropshipindia.live/dropship/track/shipment";

/* Dropship India tokens: brand blue on a light surface, site system font stack. */
const pageTokens = {
  "--color-bg": "#f5f7fa",
  "--color-ink": "#111318",
  "--color-ink-soft": "#4a5568",
  "--color-ink-muted": "#a0aec0",
  "--color-accent": "#335da9",
  "--color-accent-lt": "#eef3fb",
  "--color-accent-dk": "#1a2b6d",
  "--color-danger": "#dc3545",
  "--color-surface": "#ffffff",
  "--color-border": "#e2e8f0",
  "--font-display":
    '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
  "--font-ui":
    '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
  "--font-mono":
    'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
  backgroundColor: "#f5f7fa",
  textAlign: "left",
};

const lightTheme = createTheme();

/* ─── Styled Components ─── */

const TrackingContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  marginTop: theme.spacing(9),
  paddingBottom: theme.spacing(8),
  minHeight: "calc(100vh - 200px)",
  backgroundColor: "var(--color-bg)",
}));

const SearchBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginTop: theme.spacing(14),
  marginBottom: theme.spacing(4),
}));

const SearchPillWrapper = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  maxWidth: "580px",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-6px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "65%",
    height: "24px",
    background:
      "radial-gradient(ellipse at center, rgba(51, 93, 169, 0.22) 0%, rgba(51, 93, 169, 0.06) 55%, transparent 80%)",
    borderRadius: "50%",
    filter: "blur(10px)",
    pointerEvents: "none",
    transition: "opacity 0.4s ease, width 0.4s ease",
  },
  "&:focus-within::after": {
    width: "75%",
    background:
      "radial-gradient(ellipse at center, rgba(51, 93, 169, 0.32) 0%, rgba(51, 93, 169, 0.1) 55%, transparent 80%)",
  },
}));

const SearchPill = styled("form")(() => ({
  display: "flex",
  alignItems: "center",
  borderRadius: "60px",
  backgroundColor: "var(--color-surface)",
  border: "1.5px solid rgba(0, 0, 0, 0.06)",
  padding: "5px 6px 5px 24px",
  boxShadow: [
    "0 2px 4px rgba(0, 0, 0, 0.03)",
    "0 6px 16px rgba(0, 0, 0, 0.05)",
    "0 12px 32px rgba(0, 0, 0, 0.03)",
    "inset 0 1px 0 rgba(255, 255, 255, 0.9)",
  ].join(", "),
  transition: "box-shadow 0.35s ease, border-color 0.35s ease",
  "&:focus-within": {
    borderColor: "rgba(51, 93, 169, 0.25)",
    boxShadow: [
      "0 2px 4px rgba(0, 0, 0, 0.03)",
      "0 6px 16px rgba(0, 0, 0, 0.05)",
      "0 12px 32px rgba(0, 0, 0, 0.03)",
      "0 8px 28px rgba(51, 93, 169, 0.1)",
      "inset 0 1px 0 rgba(255, 255, 255, 0.9)",
    ].join(", "),
  },
}));

const PillInput = styled("input")(() => ({
  flex: 1,
  border: "none",
  outline: "none",
  background: "transparent",
  fontFamily: "var(--font-ui)",
  fontSize: "1rem",
  color: "var(--color-ink)",
  padding: "12px 0",
  minWidth: 0,
  "&::placeholder": {
    color: "var(--color-ink-muted)",
    fontWeight: 500,
  },
  "&:focus, &:focus-visible": {
    outline: "none",
  },
}));

const SearchIconButton = styled("button")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "46px",
  height: "46px",
  borderRadius: "50%",
  backgroundColor: "var(--color-bg)",
  border: "1px solid var(--color-border)",
  cursor: "pointer",
  transition: "all 0.25s ease",
  color: "var(--color-ink-soft)",
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "var(--color-accent)",
    borderColor: "var(--color-accent)",
    color: "#fff",
    transform: "scale(1.05)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
  "&:focus, &:focus-visible": {
    outline: "none",
  },
}));

const SectionCard = styled(Paper)(() => ({
  padding: "20px",
  borderRadius: "16px",
  boxShadow: "none",
  border: "1.5px solid var(--color-border)",
  backgroundColor: "var(--color-surface)",
}));

const StatusHero = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "tone",
})(({ tone, theme }) => ({
  position: "relative",
  overflow: "hidden",
  padding: theme.spacing(3.5),
  borderRadius: "20px",
  boxShadow: "none",
  border: "1.5px solid var(--color-border)",
  backgroundColor: "var(--color-surface)",
  marginBottom: theme.spacing(3),
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-160px",
    right: "-90px",
    width: "420px",
    height: "320px",
    background: `radial-gradient(ellipse at center, ${tone} 0%, transparent 70%)`,
    opacity: 0.11,
    pointerEvents: "none",
  },
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(2.5) },
}));

const StatusIconBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "tone" && prop !== "tint" && prop !== "edge",
})(({ tone, tint, edge, theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "52px",
  height: "52px",
  minWidth: "52px",
  borderRadius: "16px",
  color: tone,
  backgroundColor: tint,
  border: `1.5px solid ${edge}`,
  [theme.breakpoints.down("sm")]: {
    width: "44px",
    height: "44px",
    minWidth: "44px",
    borderRadius: "14px",
  },
}));

const JourneyItem = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "relative",
  paddingBottom: theme.spacing(3),
  "&:last-child": { paddingBottom: 0 },
}));

const JourneyTime = styled(Box)(({ theme }) => ({
  width: "90px",
  minWidth: "90px",
  paddingTop: "2px",
  textAlign: "right",
  paddingRight: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    width: "66px",
    minWidth: "66px",
    paddingRight: theme.spacing(2),
  },
}));

const JourneyConnector = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "101px",
  top: "8px",
  bottom: "-12px",
  width: "2px",
  background: "var(--color-border)",
  "&.last": { display: "none" },
  [theme.breakpoints.down("sm")]: {
    left: "75px",
  },
}));

const JourneyMarker = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isLatest",
})(({ isLatest, theme }) => ({
  width: isLatest ? "12px" : "8px",
  height: isLatest ? "12px" : "8px",
  borderRadius: "50%",
  background: isLatest ? "var(--color-accent)" : "var(--color-ink-muted)",
  position: "absolute",
  left: isLatest ? "96px" : "98px",
  top: isLatest ? "5px" : "7px",
  zIndex: 2,
  boxShadow: isLatest ? "0 0 0 4px rgba(51, 93, 169, 0.15)" : "none",
  [theme.breakpoints.down("sm")]: {
    left: isLatest ? "70px" : "72px",
  },
}));

const JourneyContent = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(3.5),
  paddingBottom: theme.spacing(1),
}));

/* ─── Helpers ─── */

/* Dead-end states — the shipment stopped before completing its forward or return leg. */
const NO_PROGRESS_STATUSES = [
  "CANCELLED", "CANCELLED_BY_CUSTOMER", "CANCELLED_BY_SELLER", "CANCELLED_BY_DROPSHIPPER",
  "NOT_TO_BE_FULFILLED", "DELETED", "LOST",
];

const trackOrderFaqs = [
  {
    q: "What is an AWB number?",
    a: "AWB stands for Air Waybill. It is a unique tracking number assigned to your shipment when it is booked for delivery. Your AWB number is shared with you via SMS or email once your order is dispatched.",
  },
  {
    q: "Where can I find my AWB number?",
    a: "Your AWB number is mentioned in the shipping confirmation SMS or email from the seller. If you are a Dropship India seller, you can find the AWB under the Orders section in your dashboard.",
  },
  {
    q: "How often does tracking information update?",
    a: "Tracking updates are synced from courier partners in near real-time. Updates typically reflect within 1–2 hours of a scan at a courier facility.",
  },
  {
    q: "My shipment shows Undelivered. What should I do?",
    a: "An Undelivered status means a delivery attempt was made but not completed. The courier will typically re-attempt delivery, and our NDR management system will reach out to the customer to reschedule. Write to support@dropshipindia.live if you need further assistance.",
  },
  {
    q: "How long does delivery take?",
    a: "Most orders are delivered within 5 days across India. Delivery time depends on the origin-destination combination and the courier partner selected for your shipment.",
  },
];

const getStatusColor = (status) => {
  const s = String(status || "").toUpperCase();
  if (["DELIVERED", "DELIVERED_TO_SELLER"].includes(s))
    return { bg: "rgba(76, 175, 80, 0.09)", text: "#3d8b40", border: "rgba(76, 175, 80, 0.24)" };
  if (["SHIPPED", "OUT_FOR_DELIVERY", "OUT_FOR_PICKUP", "PICKED_UP", "PICKED", "IN_TRANSIT", "CONFIRMED", "CONFIRMED_BY_DROPSHIPPER", "CONFIRMED_BY_SELLER", "FULFILLED", "APPROVED"].includes(s))
    return { bg: "rgba(51, 93, 169, 0.07)", text: "#335da9", border: "rgba(51, 93, 169, 0.2)" };
  if (["RTO_INITIATED", "RTO", "RETURNED", "UNDELIVERED", "NOT_PICKED", "QC_FAILED"].includes(s))
    return { bg: "rgba(245, 201, 0, 0.14)", text: "#b58a00", border: "rgba(245, 201, 0, 0.35)" };
  if (NO_PROGRESS_STATUSES.includes(s))
    return { bg: "rgba(220, 53, 69, 0.07)", text: "#dc3545", border: "rgba(220, 53, 69, 0.2)" };
  if (["PENDING", "PLACED", "PACKED", "LABEL_GENERATED", "LABEL_PRINTED"].includes(s))
    return { bg: "rgba(113, 128, 150, 0.08)", text: "#718096", border: "rgba(113, 128, 150, 0.22)" };
  return { bg: "rgba(0, 0, 0, 0.03)", text: "#718096", border: "#e2e8f0" };
};

const formatDate = (dateString) =>
  new Date(dateString).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const MetaDot = () => (
  <Box sx={{ width: "3px", height: "3px", borderRadius: "50%", bgcolor: "var(--color-ink-muted)", opacity: 0.55 }} />
);

/* The EDD is a calendar date serialised as UTC midnight. Formatting from the date part
   rather than parsing the timestamp keeps the day intact for a viewer west of UTC,
   where the local-time conversion would roll it back by one. */
const formatEDD = (value) => {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const getStatusIcon = (status) => {
  const s = String(status || "").toUpperCase();
  if (["DELIVERED", "DELIVERED_TO_SELLER"].includes(s)) return CheckCircleRoundedIcon;
  if (NO_PROGRESS_STATUSES.includes(s)) return CancelRoundedIcon;
  if (["RTO_INITIATED", "RTO", "RETURNED", "QC_FAILED", "UNDELIVERED", "NOT_PICKED"].includes(s))
    return ReplayRoundedIcon;
  return LocalShippingIcon;
};

const formatRelativeTime = (dateString) => {
  const minutes = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const formatStatus = (status) =>
  String(status || "")
    .trim()
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

/* ─── Component ─── */

const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [awbNumber, setAwbNumber] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTrackingData = async (awb) => {
    if (!awb.trim()) {
      setError("Please enter a valid AWB number");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${TRACKING_API}?awb=${encodeURIComponent(awb)}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch tracking information");
      const data = await response.json();
      setTrackingData(data.data);
    } catch {
      setError("Unable to find tracking information. Please check your AWB number and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const awbFromQuery = searchParams.get("awb");
    if (awbFromQuery) {
      setAwbNumber(awbFromQuery);
      fetchTrackingData(awbFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* Build journey from scan_log (preferred) or shipment_history (fallback) */
  const scanLog = Array.isArray(trackingData?.scan_log) ? trackingData?.scan_log ?? [] : [];
  const scanLogSorted = [...scanLog].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const journeyData =
    scanLogSorted.length > 0
      ? scanLogSorted
      : (trackingData?.shipment_history ?? [])
          .map((h) => ({ status: h.status, timestamp: h.timestamp, location: undefined }))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const isReturnOrder =
    trackingData?.order_type === "self_ship_return_order" || trackingData?.order_type === "seller_return_order";

  /* The API omits the field whenever no promise is worth showing — courier never
     sent one, it failed the plausibility check, or the shipment stopped moving. */
  const eddDate = trackingData?.estimated_delivery ? formatEDD(trackingData.estimated_delivery) : "";

  const currentStatus = trackingData?.shipment_history?.[0]?.status ?? journeyData[0]?.status ?? "";
  const statusColor = getStatusColor(currentStatus);
  const StatusIcon = getStatusIcon(currentStatus);
  const lastScan = journeyData[0];

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box style={pageTokens}>
        <TrackingContainer maxWidth="lg">
          {!trackingData ? (
            /* ─── Search View ─── */
            <SearchBox>
              <Typography
                variant="h4"
                component="h1"
                mb={1}
                sx={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--color-ink)" }}
              >
                Track your order
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3} sx={{ fontFamily: "var(--font-ui)" }}>
                Enter your AWB number to get real-time updates
              </Typography>
              <SearchPillWrapper>
                <SearchPill
                  onSubmit={(e) => {
                    e.preventDefault();
                    fetchTrackingData(awbNumber);
                  }}
                >
                  <PillInput
                    placeholder="Enter your tracking number / AWB number"
                    value={awbNumber}
                    onChange={(e) => setAwbNumber(e.target.value)}
                  />
                  {loading ? (
                    <Box sx={{ width: 46, height: 46, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <CircularProgress size={22} sx={{ color: "var(--color-accent)" }} />
                    </Box>
                  ) : (
                    <SearchIconButton type="submit">
                      <SearchIcon sx={{ fontSize: 22 }} />
                    </SearchIconButton>
                  )}
                </SearchPill>
                {error && (
                  <Typography variant="body2" sx={{ color: "var(--color-danger)", mt: 1.5, textAlign: "center", fontFamily: "var(--font-ui)" }}>
                    {error}
                  </Typography>
                )}
              </SearchPillWrapper>
            </SearchBox>
          ) : (
            /* ─── Results View ─── */
            <Box mt={4}>
              {/* Status Hero */}
              <StatusHero tone={statusColor.text}>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.75, md: 2.25 }, minWidth: 0 }}>
                    <StatusIconBubble tone={statusColor.text} tint={statusColor.bg} edge={statusColor.border}>
                      <StatusIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                    </StatusIconBubble>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="h1"
                        sx={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 800,
                          fontSize: { xs: "1.6rem", md: "2.15rem" },
                          lineHeight: 1.1,
                          letterSpacing: "-0.03em",
                          color: statusColor.text,
                        }}
                      >
                        {formatStatus(currentStatus) || "Awaiting Update"}
                      </Typography>
                      {lastScan?.location && (
                        <Typography
                          sx={{
                            mt: 0.75,
                            fontFamily: "var(--font-ui)",
                            fontSize: { xs: "0.85rem", md: "0.95rem" },
                            fontWeight: 600,
                            color: "var(--color-ink)",
                          }}
                        >
                          {lastScan.location}
                        </Typography>
                      )}
                      {lastScan?.timestamp && (
                        <Typography
                          sx={{
                            mt: 0.25,
                            fontFamily: "var(--font-ui)",
                            fontSize: "0.8rem",
                            color: "var(--color-ink-muted)",
                          }}
                        >
                          Updated {formatRelativeTime(lastScan.timestamp)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setTrackingData(null);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    startIcon={<SearchIcon />}
                    sx={{
                      borderRadius: "60px",
                      textTransform: "none",
                      fontFamily: "var(--font-ui)",
                      fontWeight: 600,
                      px: 2.5,
                      borderColor: "var(--color-border)",
                      color: "var(--color-ink-soft)",
                      "&:hover": {
                        borderColor: "var(--color-accent)",
                        color: "var(--color-accent)",
                        bgcolor: "var(--color-accent-lt)",
                      },
                    }}
                  >
                    Track Another
                  </Button>
                </Box>

                {eddDate && (
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "baseline",
                      gap: { xs: 1.5, md: 2.5 },
                      flexWrap: "wrap",
                      mt: { xs: 2.5, md: 3 },
                      pt: 2.25,
                      borderTop: "1px solid var(--color-border)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.11em",
                        color: "var(--color-ink-muted)",
                        flexShrink: 0,
                      }}
                    >
                      Expected Delivery
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 800,
                        fontSize: { xs: "1.15rem", md: "1.35rem" },
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                        color: "var(--color-ink)",
                      }}
                    >
                      {eddDate}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    flexWrap: "wrap",
                    mt: { xs: 3, md: 3.5 },
                    pt: 2,
                    borderTop: "1px solid var(--color-border)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.8rem",
                      color: "var(--color-ink-soft)",
                      letterSpacing: "0.04em",
                    }}
                  >
                    AWB {trackingData.awb_number}
                  </Typography>
                  {trackingData.tpl_vendor && (
                    <>
                      <MetaDot />
                      <Typography
                        sx={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: "var(--color-ink-soft)",
                        }}
                      >
                        {trackingData.tpl_vendor}
                      </Typography>
                    </>
                  )}
                  <MetaDot />
                  <Typography
                    sx={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "0.8rem",
                      color: "var(--color-ink-soft)",
                    }}
                  >
                    {isReturnOrder ? "Return" : "Order"} #{trackingData.order_id}
                  </Typography>
                  {isReturnOrder && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#9c27b0",
                        fontFamily: "var(--font-ui)",
                        px: 1,
                        py: 0.25,
                        borderRadius: "6px",
                        bgcolor: "rgba(156, 39, 176, 0.08)",
                        border: "1px solid rgba(156, 39, 176, 0.2)",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Return Order
                    </Typography>
                  )}
                </Box>
              </StatusHero>

              {/* Two-Column Layout */}
              <Grid container spacing={3}>
                {/* Left — Journey Timeline */}
                <Grid item xs={12} md={7}>
                  <SectionCard sx={{ p: { xs: 2.5, md: 3 }, display: "flex", flexDirection: "column" }}>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        mb: 2.5,
                        color: "var(--color-ink)",
                        flexShrink: 0,
                      }}
                    >
                      {isReturnOrder ? "Return Journey" : "Shipment Journey"}
                    </Typography>

                    <Box sx={{ maxHeight: "520px", overflowY: "auto", pr: 0.5, "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-thumb": { background: "var(--color-border)", borderRadius: "4px" }, "&::-webkit-scrollbar-thumb:hover": { background: "var(--color-ink-muted)" } }}>
                    {journeyData.length > 0 ? (
                      journeyData.map((scan, index) => (
                        <JourneyItem key={index}>
                          <JourneyTime>
                            <Typography
                              sx={{
                                fontFamily: "var(--font-ui)",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: index === 0 ? "var(--color-ink)" : "var(--color-ink-soft)",
                                display: "block",
                              }}
                            >
                              {new Date(scan.timestamp).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "var(--font-ui)",
                                color: "var(--color-ink-muted)",
                                display: "block",
                                fontSize: "0.7rem",
                              }}
                            >
                              {new Date(scan.timestamp).toLocaleTimeString("en-IN", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </JourneyTime>
                          <JourneyConnector className={index === journeyData.length - 1 ? "last" : ""} />
                          <JourneyMarker isLatest={index === 0} />
                          <JourneyContent>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Typography
                                sx={{
                                  fontWeight: index === 0 ? 800 : 600,
                                  color: index === 0 ? "var(--color-accent)" : "var(--color-ink)",
                                  fontFamily: "var(--font-ui)",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {formatStatus(scan.status)}
                              </Typography>
                              {index === 0 && (
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.2,
                                    bgcolor: "rgba(51, 93, 169, 0.1)",
                                    color: "var(--color-accent-dk)",
                                    borderRadius: "4px",
                                    fontSize: "0.65rem",
                                    fontFamily: "var(--font-ui)",
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                  }}
                                >
                                  Latest
                                </Box>
                              )}
                            </Box>
                            {scan.location && (
                              <Typography
                                sx={{
                                  fontFamily: "var(--font-ui)",
                                  fontSize: "0.75rem",
                                  color: "var(--color-ink-muted)",
                                  fontWeight: 500,
                                }}
                              >
                                {scan.location}
                              </Typography>
                            )}
                          </JourneyContent>
                        </JourneyItem>
                      ))
                    ) : (
                      <Typography
                        sx={{
                          color: "var(--color-ink-muted)",
                          fontFamily: "var(--font-ui)",
                          fontSize: "0.9rem",
                          py: 3,
                          textAlign: "center",
                        }}
                      >
                        No journey data available yet.
                      </Typography>
                    )}
                    </Box>
                  </SectionCard>
                </Grid>

                {/* Right — Order Details */}
                <Grid item xs={12} md={5}>
                  {/* Order Summary */}
                  <SectionCard sx={{ mb: 2.5 }}>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        mb: 2,
                        color: "var(--color-ink)",
                      }}
                    >
                      {isReturnOrder ? "Return Summary" : "Order Summary"}
                    </Typography>
                    {[
                      { label: isReturnOrder ? "Return ID" : "Order ID", value: `#${trackingData.order_id}`, mono: true },
                      {
                        label: "Total Amount",
                        value: `₹${(trackingData.order_metadata.total_amount ?? 0).toFixed(2)}`,
                        bold: true,
                      },
                      ...(!isReturnOrder ? [{ label: "Payment", value: trackingData.order_metadata.payment_mode }] : []),
                      { label: isReturnOrder ? "Return Initiated" : "Ordered", value: formatDate(trackingData.order_metadata.created_at) },
                      { label: "Last Updated", value: formatDate(trackingData.order_metadata.updated_at) },
                    ].map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1.25,
                          borderBottom: "1px solid var(--color-border)",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.82rem",
                            color: "var(--color-ink-muted)",
                            fontFamily: "var(--font-ui)",
                          }}
                        >
                          {row.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: row.bold ? 700 : 600,
                            color: "var(--color-ink)",
                            fontFamily: row.mono ? "var(--font-mono)" : "var(--font-ui)",
                            letterSpacing: row.mono ? "0.04em" : undefined,
                          }}
                        >
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                  </SectionCard>

                  {/* Line Items */}
                  {(trackingData.line_items ?? []).length > 0 && (
                    <SectionCard sx={{ mb: 2.5 }}>
                      <Typography
                        sx={{
                          fontFamily: "var(--font-display)",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                          mb: 2,
                          color: "var(--color-ink)",
                        }}
                      >
                        Items
                      </Typography>
                      {(trackingData.line_items ?? []).map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            py: 1.5,
                            borderBottom:
                              index < (trackingData.line_items?.length ?? 0) - 1
                                ? "1px solid var(--color-border)"
                                : "none",
                          }}
                        >
                          {item.product_image && (
                            <Box
                              component="img"
                              src={item.product_image}
                              alt={item.product_name}
                              onError={(e) => {
                                e.currentTarget.src = "https://placehold.co/100x100?text=Product+Image";
                              }}
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: "10px",
                                objectFit: "cover",
                                border: "1px solid var(--color-border)",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                fontFamily: "var(--font-ui)",
                                color: "var(--color-ink)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.product_name}
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                              <Typography sx={{ fontSize: "0.78rem", color: "var(--color-ink-muted)", fontFamily: "var(--font-ui)" }}>
                                Qty: {item.quantity}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.85rem",
                                  fontWeight: 700,
                                  color: "var(--color-accent)",
                                  fontFamily: "var(--font-ui)",
                                }}
                              >
                                ₹{(item.price ?? 0).toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </SectionCard>
                  )}

                  {/* Address */}
                  <SectionCard>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        mb: 2,
                        color: "var(--color-ink)",
                      }}
                    >
                      {isReturnOrder ? "Pickup Address" : "Delivery Address"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "0.9rem",
                        fontWeight: 700,
                        color: "var(--color-ink)",
                        mb: 0.5,
                      }}
                    >
                      {trackingData.customer_info.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "0.82rem",
                        color: "var(--color-ink-soft)",
                        lineHeight: 1.6,
                        mb: 0.75,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {trackingData.customer_info.address}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.8rem",
                        color: "var(--color-ink-muted)",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {trackingData.customer_info.phone_number}
                    </Typography>
                  </SectionCard>
                </Grid>
              </Grid>
            </Box>
          )}
        </TrackingContainer>

        {/* FAQ Section */}
        <Box sx={{ bgcolor: "var(--color-surface)", borderTop: "1px solid var(--color-border)", py: { xs: 5, md: 7 } }}>
          <Container maxWidth="md">
            <Typography
              component="h2"
              sx={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "2rem" },
                color: "var(--color-ink)",
                mb: 0.75,
                letterSpacing: "-0.02em",
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "1rem", color: "var(--color-ink-soft)", mb: 4 }}>
              Everything you need to know about tracking your shipment.
            </Typography>
            {trackOrderFaqs.map((item, i) => (
              <Box
                key={i}
                sx={{
                  py: 2.5,
                  borderBottom: "1px solid var(--color-border)",
                  "&:first-of-type": { borderTop: "1px solid var(--color-border)" },
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: "var(--font-ui)",
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "var(--color-ink)",
                    mb: 0.75,
                  }}
                >
                  {item.q}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "0.9375rem",
                    color: "var(--color-ink-soft)",
                    lineHeight: 1.65,
                  }}
                >
                  {item.a}
                </Typography>
              </Box>
            ))}
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TrackingPage;
